import { Check } from "lucide-react"
import type { MockCopy, MockKind } from "@/content/marketing-work"

function TemporaryCaption({
  copy,
  title,
  inverse = false,
}: {
  copy: MockCopy
  title: string
  inverse?: boolean
}) {
  return (
    <figcaption className="work-mock-caption">
      <span className="type-marketing-caption type-marketing-signal-display">
        {copy.temporaryLabel}
      </span>
      <strong className={`type-marketing-node ${inverse ? "type-marketing-inverse" : "type-marketing-ink"}`}>
        {title}
      </strong>
      <small className={`type-marketing-microcopy ${inverse ? "type-marketing-inverse-muted" : "type-marketing-muted"}`}>
        {copy.replacementNote}
      </small>
    </figcaption>
  )
}

function AccountingMock({ copy }: { copy: MockCopy }) {
  return (
    <div className="work-accounting-flow">
      <div>
        <span className="type-marketing-caption type-marketing-muted">{copy.accounting.source}</span>
        <strong className="type-marketing-node-title type-marketing-ink">#—</strong>
      </div>
      <ol>
        {copy.accounting.statuses.map((status, index) => (
          <li className="type-marketing-node type-marketing-ink" key={status}>
            <span className="type-marketing-index">{String(index + 1).padStart(2, "0")}</span>
            {status}
          </li>
        ))}
      </ol>
      <div>
        <span className="type-marketing-caption type-marketing-muted">{copy.accounting.destination}</span>
        <Check className="marketing-inline-icon" aria-hidden="true" />
      </div>
    </div>
  )
}

function StockMock({ copy }: { copy: MockCopy }) {
  return (
    <div className="work-stock-table">
      <div className="work-stock-row work-stock-header">
        {copy.stock.columns.map((column) => (
          <span className="type-marketing-caption type-marketing-muted" key={column}>{column}</span>
        ))}
      </div>
      {copy.stock.rows.map((row) => (
        <div className="work-stock-row" key={row[0]}>
          {row.map((cell, index) => (
            <span
              className={index === row.length - 1 ? "type-marketing-node type-marketing-accent-display" : "type-marketing-detail type-marketing-ink"}
              key={`${row[0]}-${cell}-${index}`}
            >
              {cell}
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}

function SupplierMock({ copy }: { copy: MockCopy }) {
  return (
    <ol className="work-supplier-flow">
      {copy.supplier.stages.map((stage, index) => (
        <li key={stage}>
          <span className="type-marketing-index">{String(index + 1).padStart(2, "0")}</span>
          <strong className="type-marketing-node type-marketing-ink">{stage}</strong>
        </li>
      ))}
    </ol>
  )
}

function AffiliateMock({ copy }: { copy: MockCopy }) {
  return (
    <div className="work-affiliate-dashboard">
      <div className="work-affiliate-metrics">
        {copy.affiliate.metrics.map((metric) => (
          <div key={metric}>
            <span className="type-marketing-caption type-marketing-muted">{metric}</span>
            <strong className="type-marketing-node-title type-marketing-ink">—</strong>
          </div>
        ))}
      </div>
      <ol>
        {copy.affiliate.rows.map((row, index) => (
          <li className="type-marketing-node type-marketing-ink" key={row}>
            <span className="type-marketing-index">{String(index + 1).padStart(2, "0")}</span>
            {row}
            <i aria-hidden="true" />
          </li>
        ))}
      </ol>
    </div>
  )
}

export function OperationsMock({
  copy,
  kind,
}: {
  copy: MockCopy
  kind: Exclude<MockKind, "storefront">
}) {
  const title = copy[kind].title

  return (
    <figure
      className={`work-operations-mock work-operations-mock-${kind}`}
      data-temporary-media="true"
      aria-label={`${copy.temporaryLabel}: ${title}`}
    >
      <div aria-hidden="true">
        <div className="work-operations-toolbar">
          <span className="type-marketing-caption type-marketing-muted">Zmile / Čajanka</span>
          <i />
        </div>
        <div className="work-operations-body">
          {kind === "accounting" && <AccountingMock copy={copy} />}
          {kind === "stock" && <StockMock copy={copy} />}
          {kind === "supplier" && <SupplierMock copy={copy} />}
          {kind === "affiliate" && <AffiliateMock copy={copy} />}
        </div>
      </div>
      <TemporaryCaption copy={copy} title={title} />
    </figure>
  )
}
